import apiClient from './client';
import type { Subscription, ApiResponse, ChangelogEntry } from '../types';

export async function getSubscriptions(): Promise<Subscription[]> {
  const response =
    await apiClient.get<ApiResponse<Subscription[]>>('/api/v1/subscriptions');
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get subscriptions');
  }
  return response.data.data;
}

/** Per-domain CNAME check (UOS only). 'legacy' = still reaches the console via the old <slug>.cloudunifi.com name. */
export interface CustomHostnameDnsStatus {
  hostname: string;
  status: 'ok' | 'legacy' | 'other';
  found: string | null;
}

export interface CustomHostnameDns {
  /** The hostname the CNAME should point to; null for a non-UOS service (nothing checked). */
  target: string | null;
  hostnames: CustomHostnameDnsStatus[];
}

export async function getCustomHostnameDns(subscriptionId: string): Promise<CustomHostnameDns> {
  const response = await apiClient.get<ApiResponse<CustomHostnameDns>>(
    `/api/v1/subscriptions/${subscriptionId}/custom-hostname/dns`
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to check custom domain DNS');
  }
  return response.data.data;
}

export interface CustomHostnameResponse {
  action_id: string;
  status: string;
  custom_hostname?: string;
}

export interface CustomHostnameWarning {
  warning: 'ip_detected' | 'wildcard_conflict';
  ip: string;
  message: string;
}

export interface CustomHostnameError {
  error: string;
  message: string;
}

// Extended API response for custom hostname endpoint (includes warning/ip fields)
interface CustomHostnameApiResponse extends ApiResponse<CustomHostnameResponse> {
  warning?: 'ip_detected' | 'wildcard_conflict';
  ip?: string;
}

export type AddCustomHostnameResult =
  | { success: true; data: CustomHostnameResponse }
  | { success: false; warning?: 'ip_detected' | 'wildcard_conflict'; ip?: string; error?: string; message: string };

export async function addCustomHostname(
  subscriptionId: string,
  hostname: string,
  confirmations: { ip_warning?: boolean; wildcard_warning?: boolean } = {}
): Promise<AddCustomHostnameResult> {
  try {
    const response = await apiClient.post<CustomHostnameApiResponse>(
      `/api/v1/subscriptions/${subscriptionId}/custom-hostname`,
      { hostname, confirm_ip_warning: confirmations.ip_warning, confirm_wildcard_warning: confirmations.wildcard_warning }
    );

    if (response.data.success) {
      return { success: true, data: response.data.data! };
    }

    return {
      success: false,
      warning: response.data.warning,
      ip: response.data.ip,
      error: response.data.error,
      message: response.data.message || 'Failed to add custom hostname',
    };
  } catch (err) {
    // Handle axios errors (4xx, 5xx responses)
    const axiosError = err as { response?: { data?: CustomHostnameApiResponse } };
    const data = axiosError.response?.data;
    return {
      success: false,
      warning: data?.warning,
      ip: data?.ip,
      error: data?.error || 'request_failed',
      message: data?.message || 'Failed to add custom hostname',
    };
  }
}

export async function patchAutoUpgrade(
  subscriptionId: string,
  enabled: boolean
): Promise<{ success: boolean; auto_upgrade: boolean }> {
  const response = await apiClient.patch<ApiResponse<{ auto_upgrade: boolean }>>(
    `/api/v1/subscriptions/${subscriptionId}/auto-upgrade`,
    { enabled }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to update auto-upgrade setting');
  }
  return { success: true, auto_upgrade: response.data.data.auto_upgrade };
}

export interface RemoveCustomHostnameDetails {
  inform_host?: string | null;
  source?: 'override' | 'legacy' | null;
  checked_at?: string | null;
  admin_override_available?: boolean;
}

export async function removeCustomHostname(
  subscriptionId: string,
  hostname: string
): Promise<{
  success: boolean;
  data?: CustomHostnameResponse;
  error?: string;
  message?: string;
  details?: RemoveCustomHostnameDetails;
}> {
  try {
    const response = await apiClient.delete<ApiResponse<CustomHostnameResponse>>(
      `/api/v1/subscriptions/${subscriptionId}/custom-hostname`,
      { data: { hostname } }
    );

    if (response.data.success) {
      return { success: true, data: response.data.data };
    }

    return {
      success: false,
      error: response.data.error,
      message: response.data.message || 'Failed to remove custom hostname',
    };
  } catch (err) {
    // Handle axios errors (4xx, 5xx responses)
    const axiosError = err as {
      response?: { data?: { error?: string; message?: string; details?: RemoveCustomHostnameDetails } };
    };
    return {
      success: false,
      error: axiosError.response?.data?.error || 'request_failed',
      message: axiosError.response?.data?.message || 'Failed to remove custom hostname',
      details: axiosError.response?.data?.details,
    };
  }
}

export async function getChangelog(subscriptionId: string): Promise<ChangelogEntry[]> {
  const response = await apiClient.get<ApiResponse<ChangelogEntry[]>>(
    `/api/v1/subscriptions/${subscriptionId}/changelog`
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get changelog');
  }
  return response.data.data;
}
